import { styled } from '@material-ui/core';

import { down } from '../../../assets/style/breakpointHelpers';
import { domTag } from '../../utils/styledFilter';

/* ============================================================
   Page shell — full-screen cream takeover (theme-aware chrome)
   ============================================================ */
export const SuccessPageWrapper = styled('div')({
	minHeight: '100dvh',
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: 'var(--color-bg-primary)',
	color: 'var(--color-text-primary)',
	paddingBottom: 'max(32px, env(safe-area-inset-bottom))',
});

export const SuccessHeader = styled('header')({
	'position': 'sticky',
	'top': 0,
	'zIndex': 20,
	'display': 'flex',
	'alignItems': 'center',
	'justifyContent': 'space-between',
	'gap': 12,
	'padding': '16px clamp(18px, 4vw, 52px)',
	'background': 'color-mix(in srgb, var(--color-bg-primary) 92%, transparent)',
	'backdropFilter': 'blur(10px)',
	'WebkitBackdropFilter': 'blur(10px)',
	'borderBottom': '1px solid color-mix(in srgb, var(--color-text-primary) 6%, transparent)',

	'& .brand': {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		background: 'none',
		border: 'none',
		padding: 0,
		cursor: 'pointer',
	},

	'& .brand-mark': {
		width: 34,
		height: 34,
		objectFit: 'contain',
		flex: 'none',
	},

	'& .brand-name': {
		fontFamily: 'var(--font-family-display)',
		fontSize: 22,
		letterSpacing: '-0.01em',
		color: 'var(--color-text-primary)',
	},

	'& .home-link': {
		'background': 'none',
		'border': 'none',
		'fontFamily': 'inherit',
		'fontSize': 14,
		'fontWeight': 500,
		'color': 'var(--color-text-secondary)',
		'padding': '12px 14px',
		'minHeight': 44,
		'cursor': 'pointer',
		'transition': 'color var(--transition-fast)',
		'&:hover': { color: 'var(--color-brand-primary)' },
	},
});

export const SuccessContent = styled('div')({
	width: '100%',
	maxWidth: 940,
	margin: '0 auto',
	padding: 'clamp(16px, 3vw, 28px) clamp(14px, 4vw, 24px) 0',
	display: 'flex',
	flexDirection: 'column',
	gap: 'clamp(18px, 3vw, 26px)',
	boxSizing: 'border-box',
});

/* ============================================================
   Hero billboard — deep-green, fixed in light & dark
   ============================================================ */
export const HeroBillboard = styled('section')({
	'position': 'relative',
	'overflow': 'hidden',
	'borderRadius': 24,
	'padding': 'clamp(30px, 5vw, 56px) clamp(22px, 4.5vw, 52px)',
	'background': '#1e5631',
	'textAlign': 'center',
	'display': 'flex',
	'flexDirection': 'column',
	'alignItems': 'center',
	'gap': 18,
	'animation': 'heroFadeUp 600ms cubic-bezier(0.22, 1, 0.36, 1) both',

	'&::after': {
		content: '""',
		position: 'absolute',
		right: '-90px',
		bottom: '-130px',
		width: 340,
		height: 340,
		borderRadius: '50%',
		background: 'rgba(247, 244, 236, 0.06)',
		pointerEvents: 'none',
	},

	'& > *': { position: 'relative', zIndex: 1 },

	'& .eyebrow': {
		fontSize: 11.5,
		fontWeight: 600,
		color: '#a8d5b4',
		letterSpacing: '0.26em',
		textTransform: 'uppercase',
	},

	'& h1': {
		'margin': 0,
		'fontFamily': 'var(--font-family-display)',
		'fontWeight': 400,
		'fontSize': 'clamp(34px, 5vw, 56px)',
		'lineHeight': 1.05,
		'letterSpacing': '-0.01em',
		'color': '#f7f4ec',
		'textWrap': 'balance',
		'& em': { fontStyle: 'italic', color: '#ffd98a' },
	},

	'& .subcopy': {
		margin: 0,
		maxWidth: 480,
		fontSize: 'clamp(15px, 1.5vw, 17px)',
		lineHeight: 1.62,
		color: 'rgba(247, 244, 236, 0.82)',
	},

	'& .hero-cta': {
		display: 'flex',
		gap: 12,
		marginTop: 4,
		flexWrap: 'wrap',
		justifyContent: 'center',
	},

	'& .btn-primary': {
		'display': 'inline-flex',
		'alignItems': 'center',
		'justifyContent': 'center',
		'gap': 9,
		'background': '#ffd98a',
		'color': '#1c2a20',
		'padding': '15px 28px',
		'borderRadius': 'var(--radius-full, 999px)',
		'fontWeight': 600,
		'letterSpacing': '0.01em',
		'fontSize': 15,
		'border': 'none',
		'fontFamily': 'inherit',
		'cursor': 'pointer',
		'minHeight': 48,
		'transition': 'filter var(--transition-fast), transform var(--transition-fast)',
		'&:hover': { filter: 'brightness(1.05)', transform: 'translateY(-1px)' },
		'&:disabled': { opacity: 0.7, cursor: 'progress', transform: 'none' },
	},

	'& .btn-ghost': {
		'display': 'inline-flex',
		'alignItems': 'center',
		'justifyContent': 'center',
		'gap': 8,
		'background': 'none',
		'border': '1.5px solid rgba(247, 244, 236, 0.4)',
		'color': '#f7f4ec',
		'padding': '15px 26px',
		'borderRadius': 'var(--radius-full, 999px)',
		'fontWeight': 600,
		'letterSpacing': '0.01em',
		'fontSize': 15,
		'fontFamily': 'inherit',
		'cursor': 'pointer',
		'minHeight': 48,
		'transition': 'border-color var(--transition-fast)',
		'&:hover': { borderColor: 'rgba(247, 244, 236, 0.85)' },
	},

	'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/* The signature element: a stamped gold wax-seal medallion carrying the party
   initials, with a verified check badge. */
export const SealMedallion = styled('div')({
	'position': 'relative',
	'width': 92,
	'height': 92,
	'marginBottom': 2,

	'& .seal-disc': {
		width: 92,
		height: 92,
		borderRadius: '50%',
		background: 'radial-gradient(circle at 32% 28%, #ffe9b8, #ffd98a 62%, #eec06a)',
		border: '3px solid rgba(30, 86, 49, 0.18)',
		boxShadow: 'inset 0 2px 6px rgba(255,255,255,0.5), 0 8px 20px rgba(0,0,0,0.22)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontFamily: 'var(--font-family-display)',
		fontSize: 26,
		letterSpacing: '0.02em',
		color: '#1e5631',
	},

	'& .seal-check': {
		position: 'absolute',
		right: -4,
		bottom: -4,
		width: 34,
		height: 34,
		borderRadius: '50%',
		background: '#1e5631',
		border: '3px solid #f7f4ec',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: '#f7f4ec',
		transformOrigin: 'center',
		animation: 'checkPop 420ms cubic-bezier(0.22, 1, 0.36, 1) 260ms both',
	},

	'@media (prefers-reduced-motion: reduce)': {
		'& .seal-check': { animation: 'none' },
	},
});

/* ============================================================
   Status pill
   ============================================================ */
const TONE_COLORS: Record<string, { bg: string; fg: string }> = {
	received: { bg: 'var(--color-success-bg, #e5f0e8)', fg: 'var(--color-success-text, #1e5631)' },
	progress: { bg: '#f8eed8', fg: '#92662a' },
	success: { bg: '#e5f0e8', fg: '#1e5631' },
	rejected: { bg: '#f5e8e8', fg: '#8f4a4a' },
};

export const StatusRow = styled('div')({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 10,
	flexWrap: 'wrap',
});

export const StatusPill = styled(domTag('span', ['tone']))(({ tone = 'received' }: { tone?: keyof typeof TONE_COLORS }) => ({
	'display': 'inline-flex',
	'alignItems': 'center',
	'gap': 8,
	'padding': '9px 16px',
	'borderRadius': 'var(--radius-full, 999px)',
	'fontSize': 13.5,
	'fontWeight': 600,
	'letterSpacing': '0.01em',
	'background': TONE_COLORS[tone].bg,
	'color': TONE_COLORS[tone].fg,

	'& .dot': {
		width: 8,
		height: 8,
		borderRadius: '50%',
		background: 'currentColor',
	},
}));

/* Shown beside the status pill only when the server echoed isTrustedAgent. */
export const TrustedTag = styled('span')({
	'display': 'inline-flex',
	'alignItems': 'center',
	'gap': 7,
	'padding': '8px 14px',
	'borderRadius': 'var(--radius-full, 999px)',
	'fontSize': 13,
	'fontWeight': 600,
	'letterSpacing': '0.01em',
	'color': 'var(--color-brand-primary)',
	'background': 'color-mix(in srgb, var(--color-brand-primary) 10%, var(--color-bg-elevated))',
	'border': '1px solid color-mix(in srgb, var(--color-brand-primary) 24%, transparent)',

	'& svg': {
		width: 16,
		height: 16,
		flex: 'none',
	},
});

export const StatusLabel = styled('span')({
	fontSize: 13,
	color: 'var(--color-text-tertiary)',
	fontWeight: 500,
});

/* ============================================================
   Card + next-steps timeline
   ============================================================ */
export const Panel = styled('section')({
	'background': 'var(--color-bg-elevated)',
	'borderRadius': 20,
	'padding': 'clamp(22px, 3.5vw, 32px)',
	'boxShadow': 'var(--shadow-sm)',
	'border': '1px solid color-mix(in srgb, var(--color-text-primary) 5%, transparent)',

	'& .panel-title': {
		margin: '0 0 20px',
		fontFamily: 'var(--font-family-display)',
		fontWeight: 400,
		fontSize: 'clamp(22px, 3vw, 28px)',
		letterSpacing: '-0.01em',
		color: 'var(--color-text-primary)',
	},
});

export const StepList = styled('ol')({
	'listStyle': 'none',
	'margin': 0,
	'padding': 0,
	'display': 'flex',
	'flexDirection': 'column',
	'gap': 4,

	'& li': {
		display: 'grid',
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		gap: 16,
		alignItems: 'start',
		padding: '14px 0',
		position: 'relative',
	},

	'& li:not(:last-child)::before': {
		content: '""',
		position: 'absolute',
		left: 17,
		top: 44,
		bottom: -2,
		width: 2,
		background: 'color-mix(in srgb, var(--color-brand-primary) 22%, transparent)',
	},

	'& .step-index': {
		width: 36,
		height: 36,
		borderRadius: '50%',
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontFamily: 'var(--font-family-display)',
		fontSize: 17,
		background: 'var(--color-success-bg, #e5f0e8)',
		color: 'var(--color-brand-primary)',
		border: '1px solid color-mix(in srgb, var(--color-brand-primary) 20%, transparent)',
	},

	'& .step-body h4': {
		margin: '6px 0 4px',
		fontSize: 15.5,
		fontWeight: 600,
		color: 'var(--color-text-primary)',
	},

	'& .step-body p': {
		margin: 0,
		fontSize: 14,
		lineHeight: 1.55,
		color: 'var(--color-text-secondary)',
	},
});

/* Application detail grid (reference, date, election, party, polling unit) */
export const DetailGrid = styled('dl')({
	'display': 'grid',
	'gridTemplateColumns': 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
	'gap': 'clamp(14px, 2.5vw, 22px)',
	'margin': 0,

	'& .detail': {
		display: 'flex',
		flexDirection: 'column',
		gap: 5,
		minWidth: 0,
	},

	'& dt': {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.12em',
		textTransform: 'uppercase',
		color: 'var(--color-text-tertiary)',
	},

	'& dd': {
		margin: 0,
		fontSize: 15.5,
		fontWeight: 500,
		color: 'var(--color-text-primary)',
		wordBreak: 'break-word',
	},

	'& dd.mono': {
		fontVariantNumeric: 'tabular-nums',
		letterSpacing: '0.02em',
	},
});

export const SupportNote = styled('p')({
	'margin': 0,
	'textAlign': 'center',
	'fontSize': 14,
	'color': 'var(--color-text-tertiary)',
	'paddingTop': 4,

	'& a': {
		color: 'var(--color-brand-primary)',
		fontWeight: 600,
		textDecoration: 'none',
	},
	'& a:hover': { textDecoration: 'underline' },
});

/* ============================================================
   Off-screen host + the certificate sheet (fixed A4 landscape,
   always cream — it is a downloadable artifact, not theme chrome)
   ============================================================ */
export const OffscreenHost = styled('div')({
	position: 'fixed',
	left: -100000,
	top: 0,
	width: 1123,
	pointerEvents: 'none',
	opacity: 0,
});

export const CertificateSheet = styled('div')({
	'width': 1123,
	'height': 794,
	'boxSizing': 'border-box',
	'padding': 30,
	'background': '#f7f4ec',
	'fontFamily': 'var(--font-family-regular)',
	'color': '#1c2a20',

	'& .cert-frame': {
		height: '100%',
		boxSizing: 'border-box',
		border: '2px solid #1e5631',
		borderRadius: 10,
		padding: '38px 56px',
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		outline: '6px solid #f7f4ec',
		outlineOffset: -12,
		boxShadow: 'inset 0 0 0 1px #d8cfb8',
	},

	'& .cert-head': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},

	'& .cert-brand': { display: 'flex', alignItems: 'center', gap: 12 },

	'& .cert-brand .mark': {
		width: 44,
		height: 44,
		objectFit: 'contain',
		flex: 'none',
	},

	'& .cert-brand .word': {
		fontFamily: 'var(--font-family-display)',
		fontSize: 26,
		lineHeight: 1,
		color: '#1e5631',
	},

	'& .cert-brand .prog': {
		fontSize: 11,
		letterSpacing: '0.16em',
		textTransform: 'uppercase',
		color: '#6b7a6f',
		marginTop: 3,
	},

	'& .cert-ref': { textAlign: 'right' },
	'& .cert-ref .ref-k': {
		fontSize: 10,
		letterSpacing: '0.16em',
		textTransform: 'uppercase',
		color: '#6b7a6f',
	},
	'& .cert-ref .ref-v': {
		fontFamily: 'var(--font-family-display)',
		fontSize: 20,
		color: '#1e5631',
		fontVariantNumeric: 'tabular-nums',
		letterSpacing: '0.03em',
	},

	'& .cert-body': {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
		gap: 10,
	},

	'& .cert-eyebrow': {
		fontSize: 13,
		fontWeight: 700,
		letterSpacing: '0.3em',
		textTransform: 'uppercase',
		color: '#92662a',
	},

	'& .cert-preline': { fontSize: 15, color: '#4a5a4f', margin: 0 },

	'& .cert-name': {
		margin: '2px 0',
		fontFamily: 'var(--font-family-display)',
		fontSize: 62,
		lineHeight: 1.05,
		color: '#1c2a20',
	},

	'& .cert-underline': {
		width: 260,
		height: 2,
		background: 'linear-gradient(90deg, transparent, #ffd98a, transparent)',
		margin: '2px 0 8px',
	},

	'& .cert-role': {
		fontSize: 22,
		fontWeight: 600,
		color: '#1e5631',
	},

	'& .cert-trusted': {
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: '0.2em',
		textTransform: 'uppercase',
		color: '#92662a',
	},

	'& .cert-context': {
		fontSize: 16,
		color: '#4a5a4f',
		maxWidth: 720,
		lineHeight: 1.5,
		margin: 0,
	},
	'& .cert-context strong': { color: '#1c2a20' },

	'& .cert-party': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 10,
		marginTop: 4,
	},
	'& .cert-party .party-seal': {
		width: 40,
		height: 40,
		borderRadius: '50%',
		background: '#e5f0e8',
		color: '#1e5631',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontWeight: 700,
		fontSize: 13,
		letterSpacing: '0.02em',
	},
	'& .cert-party .party-name': { fontSize: 16, fontWeight: 600, color: '#1c2a20' },

	'& .cert-foot': {
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'space-between',
		gap: 24,
		marginTop: 8,
	},

	'& .cert-meta': { textAlign: 'left' },
	'& .cert-meta.right': { textAlign: 'right' },
	'& .cert-meta .k': {
		fontSize: 10,
		letterSpacing: '0.14em',
		textTransform: 'uppercase',
		color: '#6b7a6f',
	},
	'& .cert-meta .v': { fontSize: 15, fontWeight: 600, color: '#1c2a20' },

	'& .cert-seal': {
		width: 72,
		height: 72,
		borderRadius: '50%',
		background: 'radial-gradient(circle at 32% 28%, #ffe9b8, #ffd98a 62%, #eec06a)',
		border: '2px solid rgba(30,86,49,0.18)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontFamily: 'var(--font-family-display)',
		fontStyle: 'italic',
		fontSize: 15,
		color: '#1e5631',
		textAlign: 'center',
		lineHeight: 1.1,
	},

	'& .cert-disclaimer': {
		marginTop: 14,
		paddingTop: 12,
		borderTop: '1px solid #d8cfb8',
		fontSize: 11.5,
		lineHeight: 1.5,
		color: '#6b7a6f',
		textAlign: 'center',
	},
});

/* ============================================================
   Fallback state — no payload (opened success URL directly)
   ============================================================ */
export const EmptyState = styled('div')({
	'flex': 1,
	'display': 'flex',
	'flexDirection': 'column',
	'alignItems': 'center',
	'justifyContent': 'center',
	'gap': 14,
	'textAlign': 'center',
	'padding': '48px 24px',

	'& h2': {
		margin: 0,
		fontFamily: 'var(--font-family-display)',
		fontWeight: 400,
		fontSize: 28,
		color: 'var(--color-text-primary)',
	},
	'& p': { margin: 0, color: 'var(--color-text-secondary)', fontSize: 15 },

	[down('sm')]: { padding: '32px 18px' },
});
